/**
 * 交割凭证上传（IR-07/08）
 * - 强制 ≥ min 张（默认 3）照片/视频
 * - 自动提取 GPS（浏览器 Geolocation）+ 与约定交割地距离校验，超出提示并允许人工说明
 */
import { useRef } from 'react';
import type { ProofItem } from '@/types';
import { formatDateTime, haversine } from '@/utils/format';

interface ProofUploadProps {
  value: ProofItem[];
  onChange: (next: ProofItem[]) => void;
  min?: number;
  max?: number;
  /** 约定交割地（GPS 校验范围依据） */
  deliveryLocation?: { lng: number; lat: number; name?: string };
  disabled?: boolean;
}

function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('读取文件失败'));
    reader.readAsDataURL(file);
  });
}

export default function ProofUpload({
  value,
  onChange,
  min = 3,
  max = 6,
  deliveryLocation,
  disabled,
}: ProofUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = async (files: FileList | null) => {
    if (!files || disabled) return;
    const next = [...value];
    for (const file of Array.from(files).slice(0, max - value.length)) {
      try {
        const url = await readFileAsDataURL(file);
        next.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          name: file.name,
          url,
          time: new Date().toISOString(),
        });
      } catch {
        /* 单文件读取失败跳过 */
      }
    }
    onChange(next);
  };

  const attachGPS = (id: string) => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onChange(
          value.map((p) =>
            p.id === id ? { ...p, lng: pos.coords.longitude, lat: pos.coords.latitude } : p,
          ),
        );
      },
      () => {
        /* 用户拒绝授权时保持无 GPS，由人工说明兜底 */
      },
      { timeout: 8000 },
    );
  };

  const removeItem = (id: string) => {
    onChange(value.filter((p) => p.id !== id));
  };

  return (
    <div className="proof-upload">
      <div className="proof-upload__hint">
        需上传 <b>{min}</b> 张以上现场照片/视频（当前 {value.length}/{min}）
        {deliveryLocation ? '，凭证将自动提取 GPS 并与约定交割地比对' : ''}
      </div>

      <div className="proof-upload__grid">
        {value.map((p) => {
          const distance =
            deliveryLocation && p.lng !== undefined && p.lat !== undefined
              ? haversine({ lng: p.lng, lat: p.lat }, { lng: deliveryLocation.lng, lat: deliveryLocation.lat })
              : null;
          const outOfRange = distance !== null && distance > 2; // 超过 2km 提示
          return (
            <div className="proof-item" key={p.id}>
              <img className="proof-item__img" src={p.url} alt={p.name} />
              <div className="proof-item__info">
                <span className="proof-item__name" title={p.name}>{p.name}</span>
                <span className="proof-item__time">{formatDateTime(p.time)}</span>
                {p.lng !== undefined && p.lat !== undefined ? (
                  <span className="proof-item__gps">
                    GPS {p.lat.toFixed(5)}, {p.lng.toFixed(5)}
                  </span>
                ) : (
                  <span className="proof-item__gps proof-item__gps--none">未定位</span>
                )}
                {outOfRange ? (
                  <span className="proof-item__warn">⚠ 距交割地约 {distance.toFixed(1)}km，超出约定范围</span>
                ) : null}
              </div>
              <div className="proof-item__ops">
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => attachGPS(p.id)}
                  disabled={disabled || p.lng !== undefined}
                >
                  定位
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => removeItem(p.id)}
                  disabled={disabled}
                >
                  删除
                </button>
              </div>
            </div>
          );
        })}

        {value.length < max ? (
          <button
            type="button"
            className="proof-item proof-item--add"
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
          >
            <span className="proof-item__plus">＋</span>
            <span>上传凭证</span>
          </button>
        ) : null}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        hidden
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = '';
        }}
      />

      {value.length < min ? (
        <div className="form-error">凭证不足 {min} 张，无法提交交割确认</div>
      ) : null}
    </div>
  );
}
